import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols/index.d.ts";
import type { CSSProperties, HTMLAttributes } from "react";
import { forwardRef } from "react";

import { useAriaAttributes } from "../../hooks/useAriaAttributes.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import style from "./SymbolIcon.module.css";

export interface SymbolProps {
  /**
   * Use the icon name, lowercase and replace spaces by "_".
   * For instance "Add Circle" becomes `"add_circle"`.
   */
  icon: MaterialSymbol;
  className?: string;
  style?: CSSProperties;
  /**
   * Also change the box size, not only the font-size.
   * @default 20
   */
  size?: number;
  /** Will change CSS font-settings FILL to 1 */
  filled?: boolean;
  domProps?: HTMLAttributes<unknown>;
}

/**
 * Material Symbol Icon.
 *
 * From https://fonts.google.com/icons?icon.style=Rounded.
 * Generated thanks to https://www.npmjs.com/package/material-symbols
 */
export const SymbolIcon = forwardRef<HTMLDivElement, SymbolProps>(function SymbolIcon(props, ref) {
  const dataAttributes = useDataAttributes(props);
  const ariaAttrs = useAriaAttributes(props);

  return (
    <div
      ref={ref}
      className={clsx(props.className, style.icon, {
        "material-symbols-rounded": typeof props.icon === "string",
        [style.filled]: props.filled,
      })}
      style={{
        ...props.style,
        ...(props.size
          ? {
              fontSize: props.size,
              width: props.size,
              height: props.size,
              flex: props.size ? `0 0 ${props.size}px` : undefined,
            }
          : {}),
      }}
      data-testid="symbol-icon"
      {...dataAttributes}
      {...ariaAttrs}
      {...props.domProps}
      data-icon={props.icon?.replace(/[ -]/g, "_").toLowerCase()}
    />
  );
});
